import React, { useEffect, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'

import InternalLink from '../../components/InternalLink'
import WarningMessage from '../components/Form/WarningMessage'

import { getAccessibleSocialPages } from '../../modules/actions/socialAccounts'
import {
  accessibleSocialPagesErrorSelector,
  accessibleSocialPagesSelector,
  accessibleSocialPagesWasLoadedSelector
} from '../../modules/selectors/socialAccounts'

import { TIKTOK_PROVIDER_PLATFORM } from '../../constants/selectLists/platformList'
import { ROUTES } from '../../constants/routes'

export function useAccessibleTikTokIdentities() {
  const dispatch = useDispatch()
  const { t } = useTranslation()

  const accessibleTikTokPages = useSelector(accessibleSocialPagesSelector)
  const accessibleSocialPagesLoadError = useSelector(accessibleSocialPagesErrorSelector)
  const accessibleSocialPagesWasLoaded = useSelector(accessibleSocialPagesWasLoadedSelector)

  const showWarningMessage =
    accessibleSocialPagesLoadError || (accessibleSocialPagesWasLoaded && accessibleTikTokPages?.length === 0)

  const TikTokIdentitiesWarningMessage = useMemo(
    () => (
      <WarningMessage
        title="Sorry, you can't upload a TikTok ad yet"
        description={
          <>
            {t('Please add a TikTok identity before creating a TikTok ad. You can do it using')}{' '}
            <InternalLink to={ROUTES.pagesAndIdentities}>{t('Settings page')}</InternalLink>.
          </>
        }
      />
    ),
    [t]
  )

  useEffect(() => {
    // tiktok identities could be loaded already for a wrapper components to check if possible to create Ad or no
    if (!accessibleSocialPagesWasLoaded) {
      dispatch(
        getAccessibleSocialPages({
          platform: TIKTOK_PROVIDER_PLATFORM
        })
      )
    }
  }, [accessibleSocialPagesWasLoaded, dispatch])

  return useMemo(
    () => ({
      showWarningMessage,
      identitiesIsLoading: !accessibleSocialPagesWasLoaded && !accessibleSocialPagesLoadError,
      TikTokIdentitiesWarningMessage
    }),
    [showWarningMessage, accessibleSocialPagesWasLoaded, accessibleSocialPagesLoadError, TikTokIdentitiesWarningMessage]
  )
}
